import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { SegmentPartnerRevenueMethod } from '@openx/types/gqlTypes.ts';
import { PlatformFeeDisplay } from './PlatformFeeDisplay.tsx';
import { PlatformFeeInput } from './PlatformFeeInput.tsx';

type CpmPricing = {
  type: typeof SegmentPartnerRevenueMethod.Cpm;
  price: number;
};

type PomPricing = {
  type: typeof SegmentPartnerRevenueMethod.PoM;
  price: number;
};

export type PricingConfig = CpmPricing | PomPricing;

export type OrganizationPricingItemProps = {
  pricing: PricingConfig;
  /** Replaces the default "Partner Fee - …" label, e.g. to tell apart summary lines of different partner types. */
  title?: string;
  /** Shows the net fee next to a read-only platform fee. */
  isAdmin?: boolean;
  canEditPlatformFee?: boolean;
  isEditMode?: boolean;
  partnerId?: string;
  platformFeeValue?: string;
  onPlatformFeeChange?: (value: string) => void;
  platformFeeError?: string | null;
};

export const OrganizationPricingItem = ({
  pricing,
  title,
  isAdmin,
  canEditPlatformFee,
  isEditMode = true,
  partnerId,
  platformFeeValue = '',
  onPlatformFeeChange,
  platformFeeError = null,
}: OrganizationPricingItemProps) => {
  const shouldShowPlatformFeeInput = canEditPlatformFee && partnerId && isEditMode && onPlatformFeeChange;
  // users who cannot edit it see it read-only; the API returns non-admins their own organization's fee only
  const shouldShowPlatformFeeDisplay = partnerId && platformFeeValue && (!isEditMode || !canEditPlatformFee);

  return (
    <IndentedSections>
      {pricing.type === SegmentPartnerRevenueMethod.Cpm && (
        <FieldDetails
          title={title ?? 'Partner Fee - Fixed CPM:'}
          dataTest="partner-fee-value"
          value={new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(pricing.price)}
        />
      )}

      {pricing.type === SegmentPartnerRevenueMethod.PoM && (
        <FieldDetails
          title={title ?? 'Partner Fee - Fixed Percentage of Media:'}
          dataTest="partner-fee-value"
          value={new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2 }).format(pricing.price)}
        />
      )}

      {shouldShowPlatformFeeInput && onPlatformFeeChange && (
        <PlatformFeeInput
          pricingType={pricing.type}
          maxPrice={pricing.price}
          value={platformFeeValue}
          onChange={onPlatformFeeChange}
          error={platformFeeError}
        />
      )}

      {shouldShowPlatformFeeDisplay && (
        <PlatformFeeDisplay
          pricingType={pricing.type}
          maxPrice={pricing.price}
          value={platformFeeValue}
          shouldShowNetFee={!!isAdmin}
        />
      )}
    </IndentedSections>
  );
};
